import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const CALL_RESPONSE = [
  ["WHO ARE WE?", "THE ROCK CREW!"],
  ["WHAT DO WE DO?", "EXPLORE. CREATE. PLAY. GROW!"],
  ["EXPLORER MODE?", "ON!"],
  ["ROCK READY?", "ALWAYS!"],
];

const SHIFT_REMINDERS = [
  ['Say "Explorer"', "not \u201ckid\u201d"],
  ['Say "Quest"', "not \u201cactivity\u201d when it fits"],
  ['Say "Basecamp"', "not \u201chomework table\u201d"],
  ['Say "Bounce Bay"', "not \u201cthe bouncy castle area\u201d"],
  ['Say "Chill Cove"', "not \u201cTV area\u201d"],
  ['Say "Explorer Fuel"', "not \u201csnack\u201d"],
];

const CHEAT = ["Rock Ready", "Adventure On", "Explorer Mode: ON", "Next Quest", "Quest Complete", "Rock Respect",
  "Caught You Rocking", "Brave Try", "Rock Reset", "Explorer Fuel", "Basecamp", "Creator's Cave",
  "Discovery Lab", "Bounce Bay", "Chill Cove", "Rock Out"];

export default function RockTalk() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isStaff = ["staff", "watcher", "driver", "admin", "manager"].includes(user?.role);
  const isAdmin = ["admin", "manager"].includes(user?.role);
  const [tab, setTab] = useState("dictionary");
  const [phrases, setPhrases] = useState([]);
  const [progress, setProgress] = useState(null);

  const load = () => {
    api.get("/rock-talk/phrases").then((r) => setPhrases(r.data)).catch(() => {});
    if (isStaff) api.get("/rock-talk/progress").then((r) => setProgress(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const markComplete = async () => {
    try { const { data } = await api.post("/rock-talk/progress", { complete: true }); setProgress(data); toast.success("Rock Talk training marked complete! 🎸"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const TABS = [
    ["dictionary", "Dictionary", "BookOpen"],
    ["callresponse", "Call & Response", "Megaphone"],
    ["flashcards", "Flashcards", "Layers"],
    ...(isStaff ? [["quiz", "Quiz", "ListChecks"]] : []),
    ["cheat", "Cheat Sheet", "Printer"],
    ...(isAdmin ? [["manage", "Manage", "Settings"], ["training", "Training", "GraduationCap"]] : []),
  ];

  return (
    <div className="min-h-screen bg-navy text-white">
      <header className="sticky top-0 z-40 h-16 flex items-center justify-between gap-2 px-4 border-b border-white/10 bg-navy print:hidden">
        <div className="flex items-center gap-2 shrink-0"><img src={LOGO} className="h-9 w-9 object-contain" alt="logo" /><div><p className="font-display font-extrabold text-sm">ROCK TALK</p><p className="text-[10px] text-turquoise tracking-widest">PLAY. LEARN. SPEAK.</p></div></div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => nav(user?.role === "parent" ? "/dashboard" : (isAdmin ? "/admin" : "/staff"))} className="text-white/70" data-testid="rocktalk-back"><Icons.ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          {user?.role !== "parent" && <Button variant="ghost" size="icon" onClick={async () => { await logout(); nav("/"); }} data-testid="rocktalk-logout">✕</Button>}
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 pb-24">
        <section className="pt-8 pb-4 text-center grain">
          <p className="font-display font-black text-3xl sm:text-4xl">ROCK TALK</p>
          <p className="text-turquoise font-bold tracking-widest text-sm mt-1">THE ADVENTURE ROCK LANGUAGE</p>
          <p className="text-white/50 text-sm mt-2 max-w-lg mx-auto">Speak the same language so every Explorer feels the Adventure Rock world. Use it naturally — you don't have to force every phrase.</p>
        </section>

        <div className="flex flex-wrap gap-1.5 justify-center mb-6 print:hidden">
          {TABS.map(([k, l, ic]) => { const Icon = Icons[ic]; return (
            <button key={k} onClick={() => setTab(k)} data-testid={`rocktalk-tab-${k}`}
              className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "bg-turquoise text-navy" : "bg-white/10 text-white/70 hover:bg-white/20"}`}>
              <Icon className="w-3.5 h-3.5" /> {l}
            </button>
          ); })}
        </div>

        {tab === "dictionary" && <Dictionary phrases={phrases} isStaff={isStaff} progress={progress} onReviewed={setProgress} />}
        {tab === "callresponse" && <CallResponse />}
        {tab === "flashcards" && <Flashcards phrases={phrases} isStaff={isStaff} onDone={setProgress} />}
        {tab === "quiz" && isStaff && <Quiz onScore={setProgress} />}
        {tab === "cheat" && <CheatSheet />}
        {tab === "manage" && isAdmin && <Manage phrases={phrases} onChange={load} />}
        {tab === "training" && isAdmin && <Training />}

        {isStaff && progress && tab !== "manage" && tab !== "training" && (
          <div className="mt-8 space-y-3 print:hidden">
            {progress.complete && progress.best_quiz_score >= 100 && (
              <div className="bg-gradient-to-br from-gold/30 to-gold/5 border-2 border-gold rounded-2xl p-4 text-center" data-testid="rocktalk-badge">
                <Icons.Award className="w-10 h-10 text-gold mx-auto mb-1" />
                <p className="font-display font-extrabold text-lg">ROCK TALK CHAMPION</p>
                <p className="text-white/70 text-sm">100% quiz + training complete. You speak fluent Adventure Rock! 🎸</p>
              </div>
            )}
            <div className="bg-white/5 border border-turquoise/30 rounded-2xl p-4 flex items-center justify-between gap-3" data-testid="rocktalk-progress">
              <div className="text-sm">
                <p className="font-bold">Your training</p>
                <p className="text-white/60 text-xs mt-0.5">Dictionary {progress.dictionary_reviewed ? "✓" : "—"} · Flashcards {progress.flashcards_completed ? "✓" : "—"} · Best quiz {progress.best_quiz_score || 0}%</p>
              </div>
              {progress.complete
                ? <span className="text-emerald-400 font-bold text-sm flex items-center gap-1" data-testid="rocktalk-complete-badge"><Icons.CheckCircle2 className="w-4 h-4" /> Complete</span>
                : <Button onClick={markComplete} className="bg-turquoise text-navy font-bold rounded-full shrink-0" data-testid="rocktalk-mark-complete">Mark training complete</Button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Dictionary({ phrases, isStaff, progress, onReviewed }) {
  const [q, setQ] = useState("");
  const [kid, setKid] = useState(false);
  useEffect(() => {
    if (isStaff && progress && !progress.dictionary_reviewed) {
      api.post("/rock-talk/progress", { dictionary_reviewed: true }).then((r) => onReviewed(r.data)).catch(() => {});
    }
  }, []); // eslint-disable-line
  const play = (p) => { if (p.audio_url) new Audio(p.audio_url).play().catch(() => {}); };
  const filtered = phrases.filter((p) => `${p.phrase} ${p.meaning} ${p.category}`.toLowerCase().includes(q.toLowerCase()));
  const cats = [...new Set(filtered.map((p) => p.category))];
  const KID_COLORS = ["from-pink-500/30 to-fuchsia-500/10 border-pink-400/40", "from-amber-400/30 to-orange-500/10 border-amber-400/40", "from-emerald-400/30 to-teal-500/10 border-emerald-400/40", "from-sky-400/30 to-blue-500/10 border-sky-400/40", "from-violet-400/30 to-purple-500/10 border-violet-400/40"];
  return (
    <div data-testid="rocktalk-dictionary">
      <div className="flex gap-2 mb-4 items-center">
        <div className="relative flex-1">
          <Icons.Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a phrase or meaning" className="rounded-xl pl-9 bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="rocktalk-dict-search" />
        </div>
        <button onClick={() => setKid((k) => !k)} data-testid="rocktalk-kid-toggle" className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-bold shrink-0 ${kid ? "bg-gold text-navy" : "bg-white/10 text-white/70"}`}><Icons.Baby className="w-4 h-4" /> Kid Mode</button>
      </div>
      {cats.map((cat, ci) => (
        <div key={cat} className="mb-6">
          <p className={`font-bold tracking-widest uppercase mb-2 ${kid ? "text-gold text-base" : "text-gold text-xs"}`}>{cat}</p>
          <div className={`grid gap-3 ${kid ? "grid-cols-1 sm:grid-cols-2" : "sm:grid-cols-2"}`}>
            {filtered.filter((p) => p.category === cat).map((p, pi) => (
              <div key={p.id} className={kid ? `bg-gradient-to-br ${KID_COLORS[(ci + pi) % KID_COLORS.length]} border-2 rounded-3xl p-5 text-center` : "bg-white/5 border border-white/10 rounded-2xl p-4"} data-testid={`rocktalk-phrase-${p.id}`}>
                <div className={kid ? "flex flex-col items-center gap-1" : "flex items-center gap-2"}>
                  <p className={`font-display font-extrabold text-turquoise ${kid ? "text-3xl" : "text-lg"}`}>{p.phrase}</p>
                  {p.audio_url && <button onClick={() => play(p)} data-testid={`rocktalk-play-${p.id}`} className="text-turquoise hover:text-white shrink-0" title="Play"><Icons.Volume2 className={kid ? "w-7 h-7" : "w-5 h-5"} /></button>}
                </div>
                <p className={`text-white/80 mt-1 ${kid ? "text-lg" : "text-sm"}`}>{p.meaning}</p>
                {!kid && p.when_to_use && <p className="text-xs text-white/50 mt-1"><b>When:</b> {p.when_to_use}</p>}
                {p.example && <p className={`text-white/50 mt-1 italic ${kid ? "text-base" : "text-xs"}`}>“{p.example}”</p>}
              </div>
            ))}
          </div>
        </div>
      ))}
      {filtered.length === 0 && <p className="text-white/40 text-center py-8">No phrases match.</p>}
    </div>
  );
}

function CallResponse() {
  return (
    <div className="space-y-3" data-testid="rocktalk-callresponse">
      <p className="text-center text-white/50 text-sm mb-4">Run this with the Rock Crew before activities to fire everyone up.</p>
      {CALL_RESPONSE.map(([call, resp], i) => (
        <div key={i} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 flex items-center gap-3"><span className="text-xs font-bold text-gold uppercase w-14 shrink-0">Staff</span><span className="font-display font-bold">{call}</span></div>
          <div className="px-4 py-3 flex items-center gap-3 bg-turquoise/15"><span className="text-xs font-bold text-turquoise uppercase w-14 shrink-0">Crew</span><span className="font-display font-extrabold text-turquoise">{resp}</span></div>
        </div>
      ))}
    </div>
  );
}

function Flashcards({ phrases, isStaff, onDone }) {
  const [i, setI] = useState(0);
  const [flip, setFlip] = useState(false);
  const [known, setKnown] = useState([]);
  const deck = useMemo(() => phrases.filter((p) => !known.includes(p.id)), [phrases, known]);
  const card = deck[i % (deck.length || 1)];
  const answer = (isKnown) => {
    if (!card) return;
    if (isKnown) {
      const nk = [...known, card.id];
      setKnown(nk); setFlip(false);
      if (nk.length === phrases.length && isStaff) {
        api.post("/rock-talk/progress", { flashcards_completed: true }).then((r) => onDone(r.data)).catch(() => {});
        toast.success("Flashcards complete! 🎉");
      }
    } else { setFlip(false); setI((v) => v + 1); }
  };
  if (!phrases.length) return <p className="text-white/40 text-center py-8">No cards yet.</p>;
  if (!card) return (
    <div className="text-center py-10" data-testid="rocktalk-flash-done">
      <Icons.PartyPopper className="w-12 h-12 text-turquoise mx-auto mb-3" />
      <p className="font-display font-bold text-xl">You know them all!</p>
      <Button onClick={() => { setKnown([]); setI(0); }} className="mt-4 bg-turquoise text-navy font-bold rounded-full">Study again</Button>
    </div>
  );
  return (
    <div className="max-w-md mx-auto" data-testid="rocktalk-flashcards">
      <p className="text-center text-white/50 text-sm mb-3">{known.length}/{phrases.length} known</p>
      <button onClick={() => setFlip((f) => !f)} className="w-full min-h-[180px] bg-white/5 border-2 border-turquoise/30 rounded-3xl p-8 flex flex-col items-center justify-center text-center" data-testid="rocktalk-flashcard">
        {!flip ? (
          <><p className="text-xs text-white/40 uppercase tracking-widest mb-2">What does this mean?</p><p className="font-display font-extrabold text-2xl text-turquoise">{card.phrase}</p><p className="text-white/40 text-xs mt-3">Tap to flip</p></>
        ) : (
          <><p className="text-xs text-white/40 uppercase tracking-widest mb-2">Meaning</p><p className="text-lg text-white">{card.meaning}</p>{card.example && <p className="text-white/50 text-sm mt-2 italic">“{card.example}”</p>}</>
        )}
      </button>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <Button onClick={() => answer(false)} variant="outline" className="rounded-full border-white/30 bg-transparent text-white" data-testid="rocktalk-flash-review">Review again</Button>
        <Button onClick={() => answer(true)} className="rounded-full bg-turquoise text-navy font-bold" data-testid="rocktalk-flash-know">I know this</Button>
      </div>
    </div>
  );
}

function Quiz({ onScore }) {
  const [qs, setQs] = useState([]);
  const [ans, setAns] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const start = () => { setResult(null); setAns({}); api.get("/rock-talk/quiz").then((r) => setQs(r.data)).catch((e) => toast.error(apiErr(e))); };
  useEffect(() => { start(); }, []); // eslint-disable-line
  const submit = async () => {
    if (Object.keys(ans).length < qs.length) return toast.error("Answer every question first.");
    setBusy(true);
    try { const { data } = await api.post("/rock-talk/quiz/submit", { answers: ans }); setResult(data); api.get("/rock-talk/progress").then((r) => onScore(r.data)).catch(() => {}); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  if (result) return (
    <div className="text-center py-8" data-testid="rocktalk-quiz-result">
      <p className="font-display font-black text-5xl text-turquoise">{result.score}%</p>
      <p className="text-white/60 mt-2">{result.correct} of {result.total} correct</p>
      <Button onClick={start} className="mt-5 bg-turquoise text-navy font-bold rounded-full" data-testid="rocktalk-quiz-retake">Retake quiz</Button>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="rocktalk-quiz">
      {qs.map((qq, qi) => (
        <div key={qq.id} className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <p className="font-semibold mb-2">{qi + 1}. {qq.question}</p>
          <div className="grid gap-2">
            {qq.options.map((op) => (
              <button key={op} onClick={() => setAns({ ...ans, [qq.id]: op })} data-testid={`quiz-${qq.id}-opt`}
                className={`text-left text-sm rounded-xl border px-3 py-2 ${ans[qq.id] === op ? "bg-turquoise/20 border-turquoise text-white" : "border-white/15 text-white/70 hover:border-white/40"}`}>{op}</button>
            ))}
          </div>
        </div>
      ))}
      <Button onClick={submit} disabled={busy} className="w-full bg-turquoise text-navy font-bold rounded-full" data-testid="rocktalk-quiz-submit">{busy ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : "Submit quiz"}</Button>
    </div>
  );
}

function CheatSheet() {
  return (
    <div data-testid="rocktalk-cheat">
      <div className="flex justify-end mb-3 print:hidden"><Button onClick={() => window.print()} className="bg-turquoise text-navy font-bold rounded-full" data-testid="rocktalk-print"><Icons.Printer className="w-4 h-4 mr-1" /> Print</Button></div>
      <div className="bg-white text-navy rounded-2xl p-6 print:shadow-none" id="rock-cheat">
        <p className="font-display font-black text-2xl text-center">ROCK TALK — QUICK CHEAT SHEET</p>
        <p className="text-center text-slate-500 text-sm mb-4">The phrases to use every shift</p>
        <div className="grid grid-cols-2 gap-2">
          {CHEAT.map((c) => (<div key={c} className="border border-slate-200 rounded-lg px-3 py-2 font-semibold text-sm">{c}</div>))}
        </div>
      </div>
    </div>
  );
}

function Manage({ phrases, onChange }) {
  const [sub, setSub] = useState("phrases");
  const [quiz, setQuiz] = useState([]);
  const [all, setAll] = useState([]);
  const blankP = { phrase: "", meaning: "", when_to_use: "", example: "", category: "General", active: true };
  const [np, setNp] = useState(blankP);
  const blankQ = { question: "", options: ["", "", "", ""], answer: "", active: true };
  const [nq, setNq] = useState(blankQ);
  const load = () => { api.get("/admin/rock-talk/phrases").then((r) => setAll(r.data)); api.get("/admin/rock-talk/quiz").then((r) => setQuiz(r.data)); };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const addP = async () => { if (!np.phrase.trim()) return toast.error("Enter a phrase."); try { await api.post("/admin/rock-talk/phrases", np); setNp(blankP); toast.success("Phrase added"); load(); onChange(); } catch (e) { toast.error(apiErr(e)); } };
  const saveP = async (p) => { try { await api.put(`/admin/rock-talk/phrases/${p.id}`, p); toast.success("Saved"); load(); onChange(); } catch (e) { toast.error(apiErr(e)); } };
  const delP = async (id) => { try { await api.delete(`/admin/rock-talk/phrases/${id}`); load(); onChange(); } catch (e) { toast.error(apiErr(e)); } };
  const setPOD = async (id) => { try { await api.post("/admin/rock-talk/phrase-of-day", { phrase_id: id }); toast.success("Set as today's Rock Talk"); } catch (e) { toast.error(apiErr(e)); } };
  const addQ = async () => { if (!nq.question.trim() || !nq.answer) return toast.error("Enter a question and mark the correct answer."); try { await api.post("/admin/rock-talk/quiz", nq); setNq(blankQ); toast.success("Question added"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const delQ = async (id) => { try { await api.delete(`/admin/rock-talk/quiz/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="text-white" data-testid="rocktalk-manage">
      <div className="flex gap-2 mb-4">
        <button onClick={() => setSub("phrases")} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${sub === "phrases" ? "bg-turquoise text-navy" : "bg-white/10 text-white/70"}`} data-testid="manage-sub-phrases">Phrases</button>
        <button onClick={() => setSub("quiz")} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${sub === "quiz" ? "bg-turquoise text-navy" : "bg-white/10 text-white/70"}`} data-testid="manage-sub-quiz">Quiz</button>
      </div>
      {sub === "phrases" ? (
        <div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-4 grid sm:grid-cols-2 gap-2">
            <Input value={np.phrase} onChange={(e) => setNp({ ...np, phrase: e.target.value })} placeholder="Phrase" className="bg-white/10 border-white/20 text-white rounded-lg" data-testid="new-phrase-text" />
            <Input value={np.category} onChange={(e) => setNp({ ...np, category: e.target.value })} placeholder="Category" className="bg-white/10 border-white/20 text-white rounded-lg" data-testid="new-phrase-cat" />
            <Input value={np.meaning} onChange={(e) => setNp({ ...np, meaning: e.target.value })} placeholder="Meaning" className="bg-white/10 border-white/20 text-white rounded-lg sm:col-span-2" data-testid="new-phrase-meaning" />
            <Input value={np.example} onChange={(e) => setNp({ ...np, example: e.target.value })} placeholder="Example (optional)" className="bg-white/10 border-white/20 text-white rounded-lg sm:col-span-2" data-testid="new-phrase-example" />
            <Button onClick={addP} className="bg-turquoise text-navy font-bold rounded-full sm:col-span-2" data-testid="new-phrase-add">Add phrase</Button>
          </div>
          <div className="space-y-2">
            {all.map((p) => (
              <div key={p.id} className={`bg-white/5 border rounded-xl p-3 ${p.active ? "border-white/10" : "border-white/5 opacity-50"}`} data-testid={`manage-phrase-${p.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0"><p className="font-bold text-turquoise">{p.phrase}</p><p className="text-xs text-white/60">{p.meaning}</p></div>
                  <div className="flex items-center gap-1 shrink-0">
                    <AudioManager phrase={p} onSaved={load} />
                    <Button size="sm" variant="ghost" className="text-white/60 h-7 px-2" onClick={() => setPOD(p.id)} data-testid={`pod-${p.id}`} title="Set phrase of the day"><Icons.Star className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-white/60 h-7 px-2" onClick={() => saveP({ ...p, active: !p.active })} data-testid={`retire-${p.id}`}>{p.active ? "Retire" : "Restore"}</Button>
                    <Button size="sm" variant="ghost" className="text-red-400 h-7 px-2" onClick={() => delP(p.id)} data-testid={`del-phrase-${p.id}`}><Icons.Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-4 space-y-2">
            <Input value={nq.question} onChange={(e) => setNq({ ...nq, question: e.target.value })} placeholder="Question" className="bg-white/10 border-white/20 text-white rounded-lg" data-testid="new-quiz-q" />
            {nq.options.map((op, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <input type="radio" name="correct" checked={nq.answer === op && op !== ""} onChange={() => setNq({ ...nq, answer: op })} data-testid={`new-quiz-correct-${oi}`} />
                <Input value={op} onChange={(e) => { const o = [...nq.options]; const wasAns = nq.answer === o[oi]; o[oi] = e.target.value; setNq({ ...nq, options: o, answer: wasAns ? e.target.value : nq.answer }); }} placeholder={`Option ${oi + 1}`} className="bg-white/10 border-white/20 text-white rounded-lg" data-testid={`new-quiz-opt-${oi}`} />
              </div>
            ))}
            <p className="text-xs text-white/40">Select the radio next to the correct answer.</p>
            <Button onClick={addQ} className="bg-turquoise text-navy font-bold rounded-full w-full" data-testid="new-quiz-add">Add question</Button>
          </div>
          <div className="space-y-2">
            {quiz.map((qq) => (
              <div key={qq.id} className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-start justify-between gap-2" data-testid={`manage-quiz-${qq.id}`}>
                <div className="min-w-0"><p className="font-semibold text-sm">{qq.question}</p><p className="text-xs text-turquoise mt-0.5">✓ {qq.answer}</p></div>
                <Button size="sm" variant="ghost" className="text-red-400 h-7 px-2 shrink-0" onClick={() => delQ(qq.id)} data-testid={`del-quiz-${qq.id}`}><Icons.Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AudioManager({ phrase, onSaved }) {
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async (blob, filename) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, filename);
      await api.post(`/admin/rock-talk/phrases/${phrase.id}/audio`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Audio saved");
      onSaved && onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  const toggleRec = async () => {
    if (rec) { rec.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = () => { stream.getTracks().forEach((t) => t.stop()); setRec(null); upload(new Blob(chunks, { type: "audio/webm" }), `rec-${phrase.id}.webm`); };
      mr.start(); setRec(mr);
    } catch { toast.error("Microphone not available"); }
  };
  return (
    <label className="flex items-center gap-1">
      <button type="button" onClick={toggleRec} disabled={busy} data-testid={`audio-rec-${phrase.id}`} className={`h-7 px-2 rounded ${rec ? "text-red-400 animate-pulse" : phrase.audio_url ? "text-turquoise" : "text-white/60"}`} title={rec ? "Stop recording" : "Record voice"}>
        {busy ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : rec ? <Icons.Square className="w-4 h-4" /> : <Icons.Mic className="w-4 h-4" />}
      </button>
      <input type="file" accept="audio/*" className="hidden" data-testid={`audio-file-${phrase.id}`} onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f, f.name); e.target.value = ""; }} />
      <span onClick={(e) => e.currentTarget.previousSibling.click()} className="text-white/40 hover:text-white cursor-pointer" title="Upload audio file"><Icons.Upload className="w-3.5 h-3.5" /></span>
    </label>
  );
}

function Training() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/rock-talk/training").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="rocktalk-training">
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center justify-between gap-2" data-testid={`training-row-${r.id}`}>
            <div className="min-w-0">
              <p className="font-semibold">{r.name} <span className="text-white/40 text-xs capitalize">· {r.role}</span></p>
              <p className="text-xs text-white/50 mt-0.5">Dict {r.dictionary_reviewed ? "✓" : "—"} · Cards {r.flashcards_completed ? "✓" : "—"} · Quiz {r.best_quiz_score || 0}%{r.last_training_at ? ` · ${new Date(r.last_training_at).toLocaleDateString()}` : ""}</p>
            </div>
            <span className={`text-xs font-bold rounded-full px-2.5 py-1 shrink-0 flex items-center gap-1 ${r.complete ? "bg-emerald-500/20 text-emerald-400" : "bg-white/10 text-white/50"}`}>{r.complete && r.best_quiz_score >= 100 && <Icons.Award className="w-3.5 h-3.5 text-gold" />}{r.complete ? "Complete" : "Incomplete"}</span>
          </div>
        ))}
        {rows.length === 0 && <p className="text-white/40 text-center py-8">No staff yet.</p>}
      </div>
    </div>
  );
}

export function PhraseOfDayCard() {
  const [p, setP] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/rock-talk/phrase-of-day").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return null;
  return (
    <button onClick={() => nav("/staff/rock-talk")} className="w-full text-left bg-gradient-to-br from-turquoise/20 to-turquoise/5 border border-turquoise/30 rounded-2xl p-4 mb-3 hover:border-turquoise transition-colors" data-testid="rocktalk-pod-card">
      <p className="text-[10px] font-bold text-turquoise tracking-widest uppercase">Rock Talk of the Day</p>
      <p className="font-display font-extrabold text-xl mt-1">{p.phrase}</p>
      <p className="text-white/70 text-sm mt-0.5">{p.meaning}</p>
      {p.example && <p className="text-white/40 text-xs mt-1 italic">“{p.example}”</p>}
    </button>
  );
}

export function ShiftReminderCard() {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-3" data-testid="rocktalk-shift-card">
      <p className="text-[10px] font-bold text-gold tracking-widest uppercase mb-2">Use the Adventure Rock language</p>
      <div className="grid grid-cols-1 gap-1">
        {SHIFT_REMINDERS.map(([a, b], i) => (<p key={i} className="text-sm text-white/80"><b>{a}</b> <span className="text-white/40">— {b}</span></p>))}
      </div>
    </div>
  );
}
